// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

import { isSentinel, normalizeModemCache, normalizeModemSignal } from './modemSignal';
import { firmwareBundleBlockReason } from './firmwareUpdateSafety';

/** Isolated Networks Fleet Chat client helpers (skill v1.3.0). */

export const HIGH_RISK_SEGMENTS = [
  'command',
  'at',
  'factory-reset',
  'factory_reset',
  'debug',
  'exec',
  'reboot',
];

export const SENSITIVE_SEGMENTS = [
  'credentials',
  'token',
  'password',
  'passwd',
  'private',
  'secret',
  'api-key',
  'apikey',
  'privatekey',
  'preshared',
];

export function pathSegments(pathAndQuery) {
  const path = String(pathAndQuery || '').split('?')[0];
  return path
    .split('/')
    .filter(Boolean)
    .map((s) => s.toLowerCase());
}

export function isHighRiskPath(path) {
  return pathSegments(path).some((s) => HIGH_RISK_SEGMENTS.includes(s));
}

export function isSensitivePath(path) {
  return pathSegments(path).some((s) => SENSITIVE_SEGMENTS.includes(s));
}

export function isWriteMethod(method) {
  return ['POST', 'PUT', 'PATCH', 'DELETE'].includes(String(method || '').toUpperCase());
}

export function needsConfirmation(method, path) {
  return isWriteMethod(method) || isSensitivePath(path) || isHighRiskPath(path);
}

/**
 * Incremental SSE parser. Returns the unused tail of the buffer.
 * `onEvent({ event, data })` — `data` is JSON when possible, else the raw string.
 */
export function consumeSseChunk(buffer, onEvent) {
  const parts = String(buffer || '').split('\n\n');
  const rest = parts.pop() ?? '';
  parts.forEach((block) => {
    if (!block.trim()) return;
    let event = 'message';
    const dataLines = [];
    block.split('\n').forEach((line) => {
      if (line.startsWith('event:')) {
        event = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).replace(/^ /, ''));
      }
    });
    if (!dataLines.length) return;
    const raw = dataLines.join('\n');
    let data = raw;
    try {
      data = JSON.parse(raw);
    } catch {
      /* keep text */
    }
    onEvent({ event, data });
  });
  return rest;
}

export function buildApproveBody({ message, history, pendingId, highRisk }) {
  return {
    message: message || '',
    history: Array.isArray(history) ? history : [],
    pending_id: pendingId,
    high_risk: Boolean(highRisk),
  };
}

export const FLEET_CHAT_ATTACH_ACCEPT = '.raucb,.tar';
export const FLEET_CHAT_ATTACH_MIN = 1024 * 1024;
export const FLEET_CHAT_ATTACH_MAX = 500 * 1024 * 1024;

export function classifyFleetChatAttachKind(filename) {
  const name = String(filename || '').toLowerCase();
  if (name.endsWith('.raucb')) return 'firmware';
  if (name.endsWith('.tar')) return 'container';
  return '';
}

export function formatFleetChatFileSize(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '0 B';
  if (n < 1024) return `${Math.round(n)} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function validateFleetChatAttachFile(file) {
  if (!file || typeof file.name !== 'string') return 'Choose a .raucb or .tar file.';
  const kind = classifyFleetChatAttachKind(file.name);
  if (!kind) return 'Only .raucb firmware or .tar container files are supported.';
  if (kind === 'firmware') {
    const blocked = firmwareBundleBlockReason(file.name);
    if (blocked) return blocked;
  }
  if (file.size < FLEET_CHAT_ATTACH_MIN) return 'File is too small (minimum 1MB).';
  if (file.size > FLEET_CHAT_ATTACH_MAX) return 'File is too large (maximum 500MB).';
  return '';
}

/** Allowlisted metadata only — never File, bytes, or data URLs. */
export function sanitizeFleetChatAttachment(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const stageId = String(raw.stage_id || '').trim();
  const name = String(raw.name || raw.filename || '').trim().slice(0, 200);
  const kind =
    raw.kind === 'firmware' || raw.kind === 'container'
      ? raw.kind
      : classifyFleetChatAttachKind(name);
  if (!stageId || !name || !kind) return null;
  const size = Number(raw.size);
  return {
    name,
    size: Number.isFinite(size) && size >= 0 ? Math.round(size) : 0,
    kind,
    stage_id: stageId.slice(0, 80),
  };
}

export function sanitizeFleetChatPendingPreview(preview) {
  if (preview == null) return undefined;
  if (typeof preview === 'string') return preview.slice(0, 4000);
  if (typeof preview !== 'object' || Array.isArray(preview)) return undefined;
  const out = {};
  ['kind', 'filename', 'image', 'sha256'].forEach((key) => {
    if (typeof preview[key] === 'string') out[key] = preview[key].slice(0, 200);
  });
  if (typeof preview.size === 'number' && Number.isFinite(preview.size)) {
    out.size = Math.round(preview.size);
  }
  if (preview.auto_reboot === true) out.auto_reboot = true;
  if (preview.registry_import === true) out.registry_import = true;
  if (Array.isArray(preview.labels)) {
    out.labels = preview.labels.slice(0, 40).map((s) => String(s).slice(0, 80));
  }
  if (Array.isArray(preview.ips)) {
    out.ips = preview.ips.slice(0, 40).map((s) => String(s).slice(0, 64));
  }
  return Object.keys(out).length ? out : undefined;
}

function fleetChatPreviewLabels(pending) {
  const labels = pending?.preview?.labels;
  if (Array.isArray(labels) && labels.length) return labels.join(', ');
  return pending?.host || 'dashboard';
}

export function isFleetChatRegistryImport(preview) {
  if (!preview || typeof preview !== 'object') return false;
  return preview.kind === 'container' && (preview.registry_import === true || (Boolean(preview.filename) && !preview.image));
}

export function fleetChatDeployConfirmTitle(pending) {
  const preview = pending?.preview;
  if (preview?.kind === 'firmware') {
    return `Confirm firmware flash on ${fleetChatPreviewLabels(pending)}`;
  }
  if (isFleetChatRegistryImport(preview)) {
    return 'Confirm container import into the dashboard registry';
  }
  if (preview?.kind === 'container') {
    return `Confirm container pull on ${fleetChatPreviewLabels(pending)}`;
  }
  return `Confirm ${pending?.method || ''} ${pending?.path || ''} on ${pending?.host || ''}`.trim();
}

export function fleetChatDeployAckLabel(pending) {
  const preview = pending?.preview;
  if (preview?.kind === 'firmware') {
    return 'I understand this will flash firmware on the listed Stargates';
  }
  if (isFleetChatRegistryImport(preview)) {
    return 'I understand this will import a container image into the dashboard registry';
  }
  if (preview?.kind === 'container') {
    return 'I understand this will pull a container image onto the listed Stargates';
  }
  return 'I understand this is a high-risk command, AT, reset, or reboot';
}

export function stageFleetChatFile({
  file,
  token,
  tokenFor,
  onProgress,
  xhrImpl,
}) {
  const bearer = typeof tokenFor === 'function' ? String(tokenFor() || '') : String(token || '');
  const Xhr = xhrImpl || (typeof XMLHttpRequest !== 'undefined' ? XMLHttpRequest : null);
  if (!Xhr) {
    return Promise.reject(new Error('Upload is not available in this browser.'));
  }
  return new Promise((resolve, reject) => {
    const xhr = new Xhr();
    xhr.open('POST', '/api/assistant/fleet-chat/stage');
    xhr.timeout = 3600000;
    xhr.setRequestHeader('Authorization', `Bearer ${bearer}`);
    xhr.upload.onprogress = (event) => {
      if (!onProgress || !event.lengthComputable) return;
      onProgress({
        loaded: event.loaded,
        total: event.total,
        percent: Math.round((event.loaded * 100) / event.total),
      });
    };
    xhr.onload = () => {
      let body = {};
      try {
        body = JSON.parse(xhr.responseText || '{}');
      } catch {
        body = {};
      }
      if (xhr.status === 401) {
        reject(new Error('Session expired. Sign in again.'));
        return;
      }
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new Error(body.detail || body.message || `Upload HTTP ${xhr.status}`));
        return;
      }
      const clean = sanitizeFleetChatAttachment({
        stage_id: body.stage_id,
        name: body.filename || file?.name,
        size: body.size ?? file?.size,
        kind: body.kind,
      });
      if (!clean) {
        reject(new Error('Upload did not return a stage_id.'));
        return;
      }
      resolve(clean);
    };
    xhr.onerror = () => reject(new Error('Upload failed.'));
    xhr.ontimeout = () => reject(new Error('Upload timed out.'));
    const form = new FormData();
    form.append('file', file);
    xhr.send(form);
  });
}

function nonemptyField(row, key) {
  const value = row && row[key];
  if (value == null) return '';
  return String(value).trim();
}

export function isDashboardStargatesPath(path) {
  const p = String(path || '').split('?')[0];
  if (p === '/api/stargates' || p === '/api/stargates/status') return true;
  if (!p.startsWith('/api/stargates/')) return false;
  const rest = p.slice('/api/stargates/'.length);
  return rest.length > 0 && /^\d+$/.test(rest);
}

function httpStatusOk(result) {
  const status = result?.http_status;
  if (status == null) return true;
  return Number(status) >= 200 && Number(status) < 300;
}

/** Inventory rows from the list tool or a 2xx Starfleet `/api/stargates*` envelope. */
export function stargateRowsFromResult(name, result) {
  if (!result || typeof result !== 'object') return null;
  if (name === 'list_authorized_targets_tool' && Array.isArray(result.stargates)) {
    return result.stargates;
  }
  if (
    name === 'starfleet_api_tool' &&
    isDashboardStargatesPath(result.path) &&
    httpStatusOk(result)
  ) {
    if (Array.isArray(result.response)) return result.response;
    if (result.response && Array.isArray(result.response.stargates)) {
      return result.response.stargates;
    }
  }
  return null;
}

function stargateRowLabel(row) {
  const name = nonemptyField(row, 'friendly_name') || nonemptyField(row, 'serial_number');
  const ip = nonemptyField(row, 'ip_address');
  if (name && ip) return `${name} at ${ip}`;
  return name || ip;
}

/** English inventory line. Tests must use fake IPs only. */
export function formatInventoryProse(result, name = 'list_authorized_targets_tool') {
  const rows = stargateRowsFromResult(name, result);
  if (!rows) return '';
  if (!rows.length) return 'No Stargates in inventory.';
  const bits = rows.map(stargateRowLabel).filter(Boolean);
  if (!bits.length) return '';
  const n = bits.length;
  return `${n} Stargate${n === 1 ? '' : 's'}: ${bits.join(', ')}.`;
}

function liveLabel(raw) {
  if (raw == null) return '';
  const text = String(raw).trim();
  if (!text || isSentinel(text)) return '';
  return text;
}

export function formatModemSignalProse(result) {
  if (!result || !httpStatusOk(result)) return '';
  const path = String(result.path || '').split('?')[0];
  if (path !== '/api/modem/signal' && path !== '/api/modem/cache') return '';
  const signal = normalizeModemSignal(result.response || result);
  const parts = [];
  if (signal.rssi != null) parts.push(`RSSI ${signal.rssi} dBm`);
  if (signal.rsrp != null) parts.push(`RSRP ${signal.rsrp} dBm`);
  if (signal.rsrq != null) parts.push(`RSRQ ${signal.rsrq} dB`);
  if (signal.sinr != null) parts.push(`SINR ${signal.sinr} dB`);
  return parts.length ? parts.join(', ') : '';
}

function hasLiveIdentity(info) {
  if (!info || typeof info !== 'object') return false;
  return ['model', 'imei', 'interface', 'ip_address', 'status'].some((k) => liveLabel(info[k]));
}

function modemInfoSource(result) {
  const root = result.response || result;
  const cache = normalizeModemCache(root);
  if (hasLiveIdentity(cache.info)) return cache.info;
  if (root && typeof root.data === 'object' && root.data) return root.data;
  return root || {};
}

export function formatModemInfoProse(result) {
  if (!result || !httpStatusOk(result)) return '';
  const path = String(result.path || '').split('?')[0];
  if (path !== '/api/modem/info' && path !== '/api/modem/cache') return '';
  const src = modemInfoSource(result);
  const parts = [];
  const model = liveLabel(src.model);
  const imei = liveLabel(src.imei);
  const iface = liveLabel(src.interface);
  const ip = liveLabel(src.ip_address);
  const status = liveLabel(src.status);
  if (model) parts.push(model);
  if (imei) parts.push(`IMEI ${imei}`);
  if (iface) parts.push(iface);
  if (ip) parts.push(ip);
  if (status) parts.push(status);
  if (!parts.length) {
    return path === '/api/modem/info' ? 'no IMEI or model reported' : '';
  }
  if (!model && !imei) return `${parts.join(', ')}; no IMEI reported`;
  return parts.join(', ');
}

export function formatModemNetworkProse(result) {
  if (!result || !httpStatusOk(result)) return '';
  const path = String(result.path || '').split('?')[0];
  if (path !== '/api/modem/network' && path !== '/api/modem/cache') return '';
  const cache = normalizeModemCache(result.response || result);
  const src = cache.network && Object.keys(cache.network).length
    ? cache.network
    : (result.response && result.response.data) || result.response || result;
  const parts = [];
  const op = liveLabel(src.operator || src.carrier);
  const kind = liveLabel(src.type || src.network_type);
  const band = liveLabel(src.band);
  const iface = liveLabel(src.interface);
  const ip = liveLabel(src.ip_address);
  const status = liveLabel(src.status);
  if (op) parts.push(op);
  if (kind) parts.push(kind);
  if (band) parts.push(`band ${band}`);
  if (iface) parts.push(iface);
  if (ip) parts.push(ip);
  if (status) parts.push(status);
  return parts.join(', ');
}

function catalogRoot(result) {
  const root = result.response || result;
  if (root == null || typeof root !== 'object' || Array.isArray(root)) return root || {};
  return root.data && typeof root.data === 'object' && !Array.isArray(root.data) ? root.data : root;
}

function firstLive(src, keys) {
  if (!src || typeof src !== 'object') return '';
  for (const key of keys) {
    const v = liveLabel(src[key]);
    if (v) return v;
  }
  return '';
}

export function formatCatalogProse(result) {
  if (!result || !httpStatusOk(result)) return '';
  const path = String(result.path || '').split('?')[0];
  const src = catalogRoot(result);
  if (path === '/api/system/info') {
    const parts = [];
    const host = liveLabel(src.hostname);
    if (host) parts.push(host);
    if (typeof src.uptime === 'number') parts.push(`uptime ${Math.round(src.uptime)}s`);
    const kernel = liveLabel(src.kernel);
    if (kernel) parts.push(`kernel ${kernel}`);
    return parts.length ? parts.join(', ') : 'system info had no hostname/uptime';
  }
  if (path === '/api/system/device') {
    return firstLive(src, ['display_name', 'device_model']) || 'no hardware model reported';
  }
  if (path === '/api/system/health') {
    const st = liveLabel(src.status) || 'unknown';
    const summary = liveLabel(src.summary);
    return summary ? `system health ${st} — ${summary}` : `system health ${st}`;
  }
  if (path === '/api/system/ui-version') {
    const v = firstLive(src, ['version', 'current_version']);
    return v ? `UI version ${v}` : 'no UI version reported';
  }
  if (path === '/api/network/interfaces') {
    const list = Array.isArray(src.interfaces) ? src.interfaces : [];
    if (!list.length) return 'no network interfaces reported';
    const names = list.map((i) => liveLabel(i && (i.name || i.interface))).filter(Boolean);
    return names.length
      ? `${list.length} interfaces (${names.join(', ')})`
      : `${list.length} network interfaces`;
  }
  if (path === '/api/network/routes') {
    if (typeof src.routes === 'string') {
      const n = src.routes.split('\n').filter((l) => l.trim()).length;
      return `${n} routes reported`;
    }
    if (Array.isArray(src.routes)) return `${src.routes.length} routes reported`;
    return 'no routes reported';
  }
  if (path === '/api/network/cellular') {
    if (src == null || typeof src !== 'object' || Array.isArray(src)) {
      return 'no cellular interface reported';
    }
    const name = liveLabel(src.name || src.interface);
    const status = liveLabel(src.status);
    if (!name && !status) return 'no cellular interface reported';
    return [name, status, src.is_primary ? 'primary uplink' : ''].filter(Boolean).join(', ');
  }
  if (path === '/api/vlan/health') {
    const ns = Array.isArray(src.active_namespaces) ? src.active_namespaces.length : 0;
    if (src.wireguard_manager_active === true) {
      return `VLAN manager running, ${ns} namespaces`;
    }
    if (src.wireguard_manager_active === false) {
      return `VLAN manager not running, ${ns} namespaces`;
    }
    return `VLAN health returned, ${ns} namespaces`;
  }
  if (path === '/api/vlan/clients') {
    const n = Array.isArray(src.clients) ? src.clients.length : 0;
    return `${n} VLAN clients`;
  }
  if (path === '/api/vlan/requirements') {
    if (src.ready_for_vlan === true) return 'ready for VLAN';
    if (src.ready_for_vlan === false) return 'not ready for VLAN';
    return 'VLAN requirements returned without ready_for_vlan';
  }
  if (path === '/api/vpn/status') {
    const parts = [];
    if (src.connected === true) parts.push('connected');
    if (src.connected === false) parts.push('not connected');
    if (src.handshake_ok === true) parts.push('handshake ok');
    if (src.handshake_ok === false) parts.push('handshake down');
    const iface = liveLabel(src.primary_interface);
    if (iface) parts.push(iface);
    return parts.length ? parts.join(', ') : 'VPN status had no connection/handshake fields';
  }
  if (path === '/api/snmp/health') {
    const st = liveLabel(src.status) || 'unknown';
    const message = liveLabel(src.message);
    return message ? `SNMP ${st} — ${message}` : `SNMP ${st}`;
  }
  if (path === '/api/firmware/version') {
    const v = firstLive(src, ['current_version', 'version', 'firmware_version', 'fw_version']);
    return v ? `firmware ${v}` : 'no firmware version reported';
  }
  if (path === '/api/mqtt/status') {
    if (src.manager_running === true) return 'MQTT manager running';
    if (src.manager_running === false) return 'MQTT manager not running';
    return 'MQTT status had no manager_running field';
  }
  if (path === '/api/wireguard-metrics/health') {
    const st = liveLabel(src.status) || 'unknown';
    return `WireGuard metrics ${st}`;
  }
  if (path === '/api/wireguard-metrics/devices') {
    const n = src.total_count ?? (Array.isArray(src.devices) ? src.devices.length : 0);
    return `${n} devices in WireGuard metrics`;
  }
  if (/^\/api\/wireguard-metrics\/device\/[^/]+\/latest$/.test(path)) {
    const n = Array.isArray(src.metrics) ? src.metrics.length : 0;
    return n === 0 ? 'No handshake samples.' : `${n} latest handshake samples.`;
  }
  return '';
}

/** One-line tool result for the transcript. Raw JSON stays in `raw`, not here. */
export function formatFleetChatResult(name, result) {
  const inventory = formatInventoryProse(result, name);
  if (inventory) return inventory;
  const path = result && result.path ? String(result.path).split('?')[0] : '';
  const info = formatModemInfoProse(result);
  const signal = formatModemSignalProse(result);
  const network = formatModemNetworkProse(result);
  const modem = path === '/api/modem/cache'
    ? [info, signal, network].filter(Boolean).join('; ')
    : signal || info || network;
  if (modem) return modem;
  if (path === '/api/modem/cache') {
    const cache = normalizeModemCache(result.response || result);
    if (cache.info && (cache.info.model != null || cache.info.imei != null)) {
      return 'no IMEI or model reported';
    }
  }
  const catalog = formatCatalogProse(result);
  if (catalog) return catalog;
  if (result && typeof result === 'object') {
    if (result.error) return String(result.error);
    if (result.http_status != null) {
      const shown = result.path ? ` ${result.path}` : '';
      return `HTTP ${result.http_status}${shown}`;
    }
  }
  return name ? `result ${name}` : 'result';
}

/** Group tool/result noise under the operator turn that produced them. */
export function groupFleetChatTurns(messages) {
  const turns = [];
  let turn = null;
  const startTurn = () => {
    turn = { user: null, activity: [], replies: [] };
    turns.push(turn);
    return turn;
  };
  (messages || []).forEach((msg) => {
    if (!msg) return;
    if (msg.role === 'user') {
      startTurn();
      turn.user = msg;
      return;
    }
    if (!turn) startTurn();
    if (msg.kind === 'tool' || msg.kind === 'result') {
      turn.activity.push(msg);
      return;
    }
    turn.replies.push(msg);
  });
  return turns;
}

/** One muted line: results, plus a trailing tool that has not produced a result yet. */
export function formatTurnActivity(activity) {
  const rows = Array.isArray(activity) ? activity : [];
  const parts = [];
  rows.forEach((msg, index) => {
    if (!msg || !msg.content) return;
    const text = String(msg.content).trim();
    if (!text) return;
    if (msg.kind === 'result') {
      parts.push(text);
      return;
    }
    if (msg.kind === 'tool') {
      const hasLaterResult = rows.slice(index + 1).some((next) => next && next.kind === 'result');
      if (!hasLaterResult) parts.push(text);
    }
  });
  return parts
    .filter((text, index) => {
      if (index === 0 || text !== parts[index - 1]) return true;
      return /^HTTP \d+/.test(text);
    })
    .join(' · ');
}

export function turnActivityRaw(activity) {
  return (Array.isArray(activity) ? activity : [])
    .filter((m) => m && m.kind === 'result' && m.raw)
    .map((m) => String(m.raw).trim())
    .filter(Boolean)
    .join('\n\n');
}

export function transcriptForModel(messages) {
  return (messages || [])
    .filter((m) => m && (m.role === 'user' || m.role === 'assistant') && typeof m.content === 'string')
    .map((m) => ({ role: m.role, content: m.content.slice(0, 2000) }));
}

/** True when token text may append onto this bubble (prose, not a tool line). */
export function isProseAssistant(msg) {
  return Boolean(
    msg &&
      msg.role === 'assistant' &&
      (msg.kind == null || msg.kind === 'assistant')
  );
}

/** Transcript Thinking row: job running, no Approve card, last line is not prose. */
export function showFleetChatThinking(busy, pending, messages) {
  if (!busy || pending) return false;
  const last = Array.isArray(messages) && messages.length ? messages[messages.length - 1] : null;
  return !(isProseAssistant(last) && String(last.content || '').trim());
}

/**
 * Stream a model token into the transcript. Never concatenates onto a tool or
 * tool_result line — that produced "tool … /api/system/infoThe starfleet…".
 */
export function appendAssistantToken(messages, text) {
  const next = Array.isArray(messages) ? [...messages] : [];
  const last = next[next.length - 1];
  if (isProseAssistant(last)) {
    next[next.length - 1] = { ...last, content: `${last.content || ''}${text}` };
    return next;
  }
  next.push({ role: 'assistant', content: text });
  return next;
}

/** WireGuard URL only — never the public HTTPS hostname the browser is on. */
export function fleetChatWireguardHint(host) {
  const h = String(host || '').trim();
  if (h) {
    return `Use http://${h}/fleet-chat over WireGuard. Public HTTPS fronts time out before the local model finishes.`;
  }
  return 'Use this install’s WireGuard host URL (http://<HOST_IP>/fleet-chat). Public HTTPS fronts time out before the local model finishes.';
}

export function fleetChatWireguardHref(host) {
  const h = String(host || '').trim();
  return h ? `http://${h}/fleet-chat` : '';
}

/** True only for HTTPS on a hostname that is not this install's WireGuard IP. */
export function fleetChatOnPublicFront(pageHost, protocol, wgHost) {
  if (protocol !== 'https:') return false;
  const host = String(wgHost || '').trim();
  return !host || pageHost !== host;
}

export function fleetChatAllowPublicQuery(search) {
  try {
    return new URLSearchParams(search || '').get('allowPublicFleetChat') === '1';
  } catch {
    return false;
  }
}

export function fleetChatHttpError(status, host, pageHost, protocol) {
  const base = `Fleet Chat HTTP ${status}`;
  if (fleetChatOnPublicFront(pageHost, protocol, host) && (status === 504 || status === 502)) {
    return `${base}. ${fleetChatWireguardHint(host)}`;
  }
  return base;
}

export function fleetChatNetworkError(err, host, pageHost, protocol) {
  const msg = (err && err.message) || String(err || 'Fleet Chat request failed');
  if (
    fleetChatOnPublicFront(pageHost, protocol, host) &&
    /failed to fetch|networkerror|load failed/i.test(msg)
  ) {
    return `${msg} ${fleetChatWireguardHint(host)}`;
  }
  return msg;
}

export const FLEET_CHAT_SESSION_PREFIX = 'starfleet.fleetChat.v1.';
export const FLEET_CHAT_PENDING_TTL_MS = 300 * 1000;
export const FLEET_CHAT_MAX_MESSAGES = 80;
export const FLEET_CHAT_CONTENT_MAX = 8000;
export const FLEET_CHAT_DRAFT_MAX = 2000;

export function fleetChatSessionKey(username) {
  const name = String(username || '').trim();
  return name ? `${FLEET_CHAT_SESSION_PREFIX}${name}` : '';
}

export function formatFleetChatElapsed(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n < 0) return '';
  if (n < 10_000) return `${(n / 1000).toFixed(1)}s`;
  if (n < 60_000) return `${Math.round(n / 1000)}s`;
  const totalSec = Math.round(n / 1000);
  const minutes = Math.floor(totalSec / 60);
  const seconds = totalSec % 60;
  return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
}

export function stampTurnElapsed(messages, elapsedMs) {
  const next = Array.isArray(messages) ? [...messages] : [];
  const value = Math.max(0, Math.round(Number(elapsedMs) || 0));
  for (let i = next.length - 1; i >= 0; i -= 1) {
    if (next[i] && next[i].role === 'user') {
      next[i] = { ...next[i], elapsedMs: value };
      return next;
    }
  }
  return next;
}

export function turnElapsedMs(userMsg, now, busy) {
  if (!userMsg) return null;
  if (typeof userMsg.elapsedMs === 'number' && Number.isFinite(userMsg.elapsedMs)) {
    return userMsg.elapsedMs;
  }
  if (busy && typeof userMsg.startedAt === 'number' && Number.isFinite(userMsg.startedAt)) {
    return Math.max(0, Number(now) - userMsg.startedAt);
  }
  return null;
}

function coerceTimestamp(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Date.parse(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}

function sanitizeSessionMessage(msg) {
  if (!msg || typeof msg !== 'object') return null;
  if (msg.role !== 'user' && msg.role !== 'assistant') return null;
  if (typeof msg.content !== 'string') return null;
  const out = {
    role: msg.role,
    content: msg.content.slice(0, FLEET_CHAT_CONTENT_MAX),
  };
  if (msg.kind === 'tool' || msg.kind === 'result' || msg.kind === 'assistant') {
    out.kind = msg.kind;
  }
  const ts = coerceTimestamp(msg.timestamp);
  if (ts != null) out.timestamp = ts;
  if (typeof msg.startedAt === 'number' && Number.isFinite(msg.startedAt)) {
    out.startedAt = msg.startedAt;
  }
  if (typeof msg.elapsedMs === 'number' && Number.isFinite(msg.elapsedMs) && msg.elapsedMs >= 0) {
    out.elapsedMs = Math.round(msg.elapsedMs);
  }
  return out;
}

function sanitizePending(pending, now) {
  if (!pending || typeof pending !== 'object') return null;
  const id = String(pending.pending_id || '').trim();
  if (!id) return null;
  const savedAt = coerceTimestamp(pending.savedAt) ?? now;
  if (now - savedAt >= FLEET_CHAT_PENDING_TTL_MS) return null;
  const out = { pending_id: id, savedAt };
  ['method', 'path', 'host', 'tool'].forEach((key) => {
    if (typeof pending[key] === 'string') out[key] = pending[key].slice(0, 200);
  });
  if (pending.high_risk === true) out.high_risk = true;
  const preview = sanitizeFleetChatPendingPreview(pending.preview);
  if (preview != null) out.preview = preview;
  return out;
}

export function sanitizeFleetChatSession(raw, username, now = Date.now()) {
  const name = String(username || '').trim();
  if (!name || !raw || typeof raw !== 'object') return null;
  if (String(raw.username || '').trim() !== name) return null;
  const messages = (Array.isArray(raw.messages) ? raw.messages : [])
    .map(sanitizeSessionMessage)
    .filter(Boolean)
    .slice(-FLEET_CHAT_MAX_MESSAGES);
  const after = Number.isFinite(raw.after) ? Math.max(0, Math.floor(raw.after)) : 0;
  const jobId = typeof raw.jobId === 'string' && raw.jobId.trim() && !/\s/.test(raw.jobId.trim())
    ? raw.jobId.trim().slice(0, 80)
    : '';
  const startedAt =
    typeof raw.startedAt === 'number' && Number.isFinite(raw.startedAt) ? raw.startedAt : null;
  const input = typeof raw.input === 'string' ? raw.input.slice(0, FLEET_CHAT_DRAFT_MAX) : '';
  return {
    v: 1,
    username: name,
    messages,
    input,
    pending: sanitizePending(raw.pending, now),
    attachment: sanitizeFleetChatAttachment(raw.attachment),
    jobId: jobId || null,
    after,
    startedAt,
  };
}

export function loadFleetChatSession(storage, username) {
  const key = fleetChatSessionKey(username);
  if (!key || !storage) return null;
  try {
    const raw = storage.getItem(key);
    if (!raw) return null;
    return sanitizeFleetChatSession(JSON.parse(raw), username);
  } catch {
    return null;
  }
}

/** Live confirm card — same 300s TTL as sanitize. */
export function fleetChatPendingIsLive(pending, now = Date.now()) {
  return sanitizePending(pending, now) != null;
}

/**
 * Refuse empty transcript + live jobId (hydrate/persist clobber).
 * Clear uses removeItem, not this path.
 */
export function shouldWriteFleetChatSession(snapshot) {
  if (!snapshot || typeof snapshot !== 'object') return false;
  if (snapshot.jobId && (!Array.isArray(snapshot.messages) || snapshot.messages.length === 0)) {
    return false;
  }
  return true;
}

export function saveFleetChatSession(storage, username, snapshot) {
  const key = fleetChatSessionKey(username);
  if (!key || !storage) return false;
  const clean = sanitizeFleetChatSession({ ...snapshot, username, v: 1 }, username);
  if (!clean || !shouldWriteFleetChatSession(clean)) return false;
  try {
    storage.setItem(key, JSON.stringify(clean));
    return true;
  } catch {
    try {
      const trimmed = {
        ...clean,
        messages: clean.messages.slice(-Math.floor(FLEET_CHAT_MAX_MESSAGES / 2)),
      };
      storage.setItem(key, JSON.stringify(trimmed));
      return true;
    } catch {
      return false;
    }
  }
}

export function clearFleetChatSession(storage, username) {
  const key = fleetChatSessionKey(username);
  if (!key || !storage) return;
  try {
    storage.removeItem(key);
  } catch {
    /* private mode */
  }
}

export function fleetChatJobPollMs() {
  return 750;
}

export function sleepMs(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      const err = new Error('Aborted');
      err.name = 'AbortError';
      reject(err);
      return;
    }
    const timer = setTimeout(resolve, ms);
    if (!signal) return;
    const onAbort = () => {
      clearTimeout(timer);
      const err = new Error('Aborted');
      err.name = 'AbortError';
      reject(err);
    };
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

function abortErr() {
  const err = new Error('Aborted');
  err.name = 'AbortError';
  return err;
}

function resolveFleetChatToken(token, tokenFor) {
  if (typeof tokenFor === 'function') return String(tokenFor() || '');
  return String(token || '');
}

export async function cancelFleetChatJob({ jobId, pollUrlFor, token, tokenFor, fetchImpl = fetch }) {
  const id = String(jobId || '').trim();
  if (!id) return;
  const bearer = resolveFleetChatToken(token, tokenFor);
  if (!bearer) return;
  try {
    await fetchImpl(pollUrlFor(id), {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${bearer}`,
        Accept: 'application/json',
      },
    });
  } catch {
    /* job TTL / DoneGuard still stop the session */
  }
}

/**
 * Poll an existing Fleet Chat job. Abort stops the loop. DELETE only when
 * cancelOnAbort is true (Clear / logout). Effect remounts must pass false.
 */
export async function pollFleetChatJob({
  jobId,
  after = 0,
  pollUrlFor,
  token,
  tokenFor,
  onEvent,
  onCursor,
  signal,
  cancelOnAbort = true,
  sleep = sleepMs,
  pollMs = fleetChatJobPollMs(),
  fetchImpl = fetch,
}) {
  let cursor = Number.isFinite(after) ? Math.max(0, Math.floor(after)) : 0;
  let transientMs = 0;
  try {
    for (;;) {
      if (signal?.aborted) {
        throw abortErr();
      }
      const bearer = resolveFleetChatToken(token, tokenFor);
      if (!bearer) {
        throw new Error('Session expired. Sign in again.');
      }
      const pollRes = await fetchImpl(`${pollUrlFor(jobId)}?after=${cursor}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${bearer}`,
          Accept: 'application/json',
        },
        signal,
      });
      if (pollRes.status === 401) {
        throw new Error('Session expired. Sign in again.');
      }
      if (pollRes.status === 403 || pollRes.status === 404) {
        throw new Error('Session ended. Send again.');
      }
      if (pollRes.status === 502 || pollRes.status === 503) {
        transientMs += 2000;
        if (transientMs > 180000) {
          throw new Error(`Fleet Chat HTTP ${pollRes.status}`);
        }
        await sleep(2000, signal);
        continue;
      }
      if (!pollRes.ok) {
        throw new Error(`Fleet Chat HTTP ${pollRes.status}`);
      }
      transientMs = 0;
      const page = await pollRes.json();
      if (signal?.aborted) {
        throw abortErr();
      }
      const events = Array.isArray(page?.events) ? page.events : [];
      events.forEach((item) => {
        if (signal?.aborted) {
          throw abortErr();
        }
        onEvent({ event: item.event, data: item.data });
      });
      if (typeof page?.next === 'number') {
        cursor = page.next;
      } else {
        cursor += events.length;
      }
      onCursor?.(cursor, Boolean(page?.done));
      if (page?.done) {
        return { after: cursor, done: true };
      }
      await sleep(pollMs, signal);
    }
  } catch (err) {
    if (err?.name === 'AbortError' && cancelOnAbort) {
      await cancelFleetChatJob({ jobId, pollUrlFor, token, tokenFor, fetchImpl });
    }
    throw err;
  }
}

/**
 * Start a Fleet Chat job and poll until done. Short HTTP hops so Azure Front
 * Door / App Gateway do not 504 a long SSE POST.
 */
export async function runFleetChatJob({
  postUrl,
  pollUrlFor,
  body,
  token,
  tokenFor,
  onEvent,
  onJobId,
  onCursor,
  signal,
  cancelOnAbort = true,
  sleep = sleepMs,
  pollMs = fleetChatJobPollMs(),
  fetchImpl = fetch,
}) {
  const bearer = resolveFleetChatToken(token, tokenFor);
  const postRes = await fetchImpl(postUrl, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${bearer}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(body),
    signal,
  });
  if (postRes.status === 401) {
    throw new Error('Session expired. Sign in again.');
  }
  if (postRes.status === 404) {
    throw new Error('Fleet Chat is not enabled on this host (AI_ENABLED).');
  }
  if (!postRes.ok) {
    throw new Error(`Fleet Chat HTTP ${postRes.status}`);
  }
  const started = await postRes.json();
  const jobId = started?.job_id;
  if (!jobId) {
    throw new Error('Fleet Chat did not return a job_id.');
  }
  onJobId?.(jobId);
  return pollFleetChatJob({
    jobId,
    after: 0,
    pollUrlFor,
    token,
    tokenFor,
    onEvent,
    onCursor,
    signal,
    cancelOnAbort,
    sleep,
    pollMs,
    fetchImpl,
  });
}
